import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

import { searchAreasByLevel, type AreaSearchResult, type GeoType } from '../../../../api/regions';
import { SegmentedControl } from '../../primitives';

interface Props {
  /** The active geography level (shared with the map's click-to-add). */
  level: GeoType;
  onLevelChange: (level: GeoType) => void;
  /** Called when the user picks an area to add (carries its boundary polygon). */
  onPick: (result: AreaSearchResult) => void;
  /** Optional: preview an area on the map as the user highlights results. */
  onHover?: (result: AreaSearchResult | null) => void;
}

const LEVEL_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'county', label: 'County' },
  { value: 'city', label: 'City' },
  { value: 'neighborhood', label: 'Neighborhood' },
] as const;

const inputStyle: CSSProperties = {
  width: '100%',
  height: 34,
  padding: '0 11px',
  borderRadius: 9,
  border: '1px solid var(--nb-border)',
  background: 'var(--nb-bg)',
  font: 'inherit',
  fontSize: 13,
  color: 'var(--nb-ink)',
  boxSizing: 'border-box',
};

const listStyle: CSSProperties = {
  position: 'absolute',
  zIndex: 10,
  top: 38,
  left: 0,
  right: 0,
  margin: 0,
  padding: 4,
  listStyle: 'none',
  background: 'var(--nb-bg)',
  border: '1px solid var(--nb-border)',
  borderRadius: 9,
  boxShadow: '0 8px 24px rgba(15,25,18,0.14)',
  maxHeight: 240,
  overflow: 'auto',
};

const itemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '7px 9px',
  borderRadius: 7,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 12.5,
  color: 'var(--nb-ink)',
};

const PLACEHOLDER: Record<GeoType, string> = {
  state: 'Search a state…',
  county: 'Search a county…',
  city: 'Search a city or town…',
  neighborhood: 'Search a neighborhood…',
};

/** Debounced Nominatim search for the current query + level. */
function useAreaSearch(query: string, level: GeoType) {
  const [results, setResults] = useState<AreaSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      const hits = await searchAreasByLevel(q, level);
      if (mine === seq.current) {
        setResults(hits);
        setOpen(true);
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, level]);

  return { results, loading, open, setOpen, setResults };
}

/**
 * Region builder's "add a geography" control: pick a level (state/county/city/
 * neighborhood), then type to search Nominatim areas of that level. Picking a
 * result hands its boundary polygon to the caller.
 */
export function AreaTypePicker({ level, onLevelChange, onPick, onHover }: Props) {
  const [query, setQuery] = useState('');
  const { results, loading, open, setOpen, setResults } = useAreaSearch(query, level);

  const reset = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onHover?.(null);
  };

  const pick = (r: AreaSearchResult) => {
    onPick(r);
    reset();
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <SegmentedControl
          value={level}
          onChange={(v) => {
            onLevelChange(v as GeoType);
            reset();
          }}
          options={LEVEL_OPTIONS}
          ariaLabel="Geography type"
        />
      </div>
      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={PLACEHOLDER[level]}
          aria-label={`Search ${level}`}
          style={inputStyle}
        />
        {open && (
          <ul role="listbox" style={listStyle}>
            {results.length === 0 ? (
              <li style={{ ...itemStyle, color: 'var(--nb-subtle)', cursor: 'default' }}>
                {loading ? 'Searching…' : 'No matches'}
              </li>
            ) : (
              results.map((r) => (
                <li key={`${r.geoType}:${r.geoRef}`}>
                  <button
                    type="button"
                    onClick={() => pick(r)}
                    onMouseEnter={() => onHover?.(r)}
                    onMouseLeave={() => onHover?.(null)}
                    style={itemStyle}
                  >
                    {r.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
